import { Hub } from 'aws-amplify/utils';
import { trackSignupError } from '../analytics/signup';

/**
 * A Google or Apple sign-in that fails after the redirect. (#941)
 *
 * The failure comes back to the site's root (REACT_APP_REDIRECT_URL), where Amplify finishes
 * the flow as it configures and reports the failure once, as a Hub event. The only listener
 * was in AuthPage, which the root does not render, so it was never heard: a refused sign-up
 * (a PreSignUp "an account already exists for this email") landed on the home page with no
 * word, and the redirect half of the sign-up error count stayed empty.
 *
 * listenForRedirectFailure runs at start-up, before Amplify.configure, and hands the failure
 * to the sign-in page: straight to it when it is showing, otherwise through this tab's
 * session storage, going to /login. It is counted there, on the page that stays loaded: the
 * count queues until Plausible's script has loaded, and leaving the page first would lose it.
 */

const STORED = 'chaptery.redirectFailure';
const FALLBACK = 'Sign-in with Google or Apple did not complete. Please try again.';
const AUTH_PAGES = ['/login', '/register'];
const PRESIGNUP = /^PreSignUp failed with error\s*/i;

interface Failure {
  message: string;
  // The error's name, all the count records (trackSignupError).
  name?: string;
}

/**
 * The words to show. A sign-up our PreSignUp trigger refused carries its own message, written
 * for users (post-confirmation/index.js), behind Cognito's prefix. Anything else is Amplify's
 * or the provider's ("User cancelled OAuth flow.", "invalid_grant"), and gets ours.
 */
export function redirectFailureMessage(error: unknown): string {
  const raw = error && typeof error === 'object' && 'message' in error
    ? String((error as { message?: unknown }).message ?? '')
    : String(error ?? '');
  if (!PRESIGNUP.test(raw)) return FALLBACK;
  return raw.replace(PRESIGNUP, '').trim() || FALLBACK;
}

let showing: ((message: string) => void) | null = null;

function show(failure: Failure, to: (message: string) => void) {
  trackSignupError('redirect', { name: failure.name });
  to(failure.message);
}

export function listenForRedirectFailure(): () => void {
  /* Read now, before anything tidies the address. Every real failure comes back from Google
     or Apple with code or error in it. Amplify also reports a sign-in left unfinished earlier
     (Back from Google, or a tab closed there, then any later load in this browser), with
     nothing in the address: that is not a failed sign-up, and says nothing to anyone. */
  const returned = /[?&](code|error)=/.test(window.location.search);
  return Hub.listen('auth', ({ payload }) => {
    if (payload.event !== 'signInWithRedirect_failure' || !returned) return;
    const error = payload.data?.error;
    const failure: Failure = { message: redirectFailureMessage(error), name: error?.name };
    if (showing) {
      show(failure, showing);
      return;
    }
    try {
      sessionStorage.setItem(STORED, JSON.stringify(failure));
    } catch {
      // Storage refused (a private window, say): the sign-in page still opens, without the reason.
    }
    if (!AUTH_PAGES.includes(window.location.pathname)) window.location.replace('/login');
  });
}

/**
 * For the sign-in page: shows a failure that came before it opened, once, and any that
 * comes while it is open. Returns the unsubscribe.
 */
export function onRedirectFailure(to: (message: string) => void): () => void {
  let stored: Failure | null = null;
  try {
    const raw = sessionStorage.getItem(STORED);
    sessionStorage.removeItem(STORED);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed.message === 'string') stored = parsed;
  } catch {
    // Nothing stored that can be read.
  }
  if (stored) show(stored, to);
  showing = to;
  return () => {
    if (showing === to) showing = null;
  };
}
